import {
  type MouseEvent,
  type MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

type Props = {
  disabled?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  delay?: number;
};

const useThrottleClick = ({ disabled, delay = 2000, onClick }: Props) => {
  const [isClicked, setIsClicked] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (isClicked || disabled) return;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      setIsClicked(true);
      onClick?.(event);

      timeoutRef.current = setTimeout(() => {
        setIsClicked(false);
        timeoutRef.current = null;
      }, delay);
    },
    [onClick, disabled, delay, isClicked],
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return handleClick;
};

export { useThrottleClick };
